/**
 * ChatNotifications - Success and error notification components
 *
 * Displays floating success and error messages in the chat panel.
 * Extracted from ChatPanel.tsx to reduce complexity.
 */

import React from 'react';
import { AlertCircle, Check, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

export interface ErrorNotificationProps {
  message: string;
}

export function ErrorNotification({ message }: ErrorNotificationProps): React.ReactElement {
  return (
    <div
      className="absolute top-16 left-1/2 transform -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-2 duration-300"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-nb-error/10 border border-nb-error/20 shadow-lg">
        <AlertCircle className="w-4 h-4 text-nb-error flex-shrink-0" />
        <span className="text-sm font-medium text-nb-error">{message}</span>
      </div>
    </div>
  );
}

export interface SuccessNotificationProps {
  message: string;
  onDismiss?: () => void;
}

export function SuccessNotification({
  message,
  onDismiss,
}: SuccessNotificationProps): React.ReactElement {
  const { theme } = useTheme();

  return (
    <div
      className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-2 duration-300 pointer-events-auto"
      role="status"
      aria-live="polite"
    >
      <div
        className={`flex items-center gap-3 px-5 py-3.5 rounded-lg ${theme.colors.bg.tertiary} border ${theme.colors.border.primary} shadow-xl max-w-md backdrop-blur-sm`}
      >
        <div
          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${theme.colors.bg.secondary}`}
        >
          <Check className="w-4 h-4 text-nb-success" />
        </div>
        <span className={`text-sm font-medium flex-1 ${theme.colors.text.primary}`}>{message}</span>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`flex-shrink-0 p-1 rounded hover:${theme.colors.bg.secondary} transition-colors`}
            aria-label="Cerrar mensaje"
          >
            <X className={`w-4 h-4 ${theme.colors.text.secondary}`} />
          </button>
        )}
      </div>
    </div>
  );
}

export interface LoadingIndicatorProps {}

export function LoadingIndicator(): React.ReactElement {
  const { theme } = useTheme();

  return (
    <div className="flex justify-start" role="status" aria-live="polite">
      <div
        className={`flex items-center gap-2 rounded-xl ${theme.colors.bg.secondary} ${theme.colors.text.primary} text-sm px-4 py-2.5 border ${theme.colors.border.primary} shadow-sm`}
      >
        <div className="flex gap-1" aria-hidden="true">
          <span
            className="w-2 h-2 bg-current rounded-full animate-bounce opacity-70"
            style={{ animationDelay: '0ms' }}
          />
          <span
            className="w-2 h-2 bg-current rounded-full animate-bounce opacity-70"
            style={{ animationDelay: '150ms' }}
          />
          <span
            className="w-2 h-2 bg-current rounded-full animate-bounce opacity-70"
            style={{ animationDelay: '300ms' }}
          />
        </div>
        <span className="font-medium">Procesando...</span>
      </div>
    </div>
  );
}

export default {
  ErrorNotification,
  SuccessNotification,
  LoadingIndicator,
};
